/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import PropTypes from 'prop-types'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import dayjs from 'dayjs'
import { getDayjsTZDate } from '@sportninja/common/utils/utils'
import { countTeamShots } from '@sportninja/common/utils/games/shotsCalculator'
import { getContrastTextColor } from '@sportninja/common/utils/colorContrast'
import useVideoSyncedScore from 'src/hooks/useVideoSyncedScore'
import useGameSport from 'src/hooks/useGameSport'
import { font } from 'src/components/css'
import useScoreboardAnimations from '../hooks/useScoreboardAnimations'
import AnimatedScore from './components/AnimatedScore'
import FoulCard from './components/FoulCard'
import GoalScoredCard from './components/GoalScoredCard'
import PeriodCard from './components/PeriodCard'
import SubstitutionCard from './components/SubstitutionCard'

const widgetContainer = css`
  position: absolute;
  top: 16px;
  left: 32px;
  display: flex;
  align-items: stretch;
  background: ${colors.WHITE};
  overflow: hidden;
  z-index: 10;
  width: fit-content;
`

const logoCell = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 10px;
  flex-shrink: 0;
`

const leagueLogo = css`
  object-fit: contain;
`

const clockDivider = css`
  width: 16px;
  height: 1px;
  background: ${colors.NEUTRAL_100};
  margin: 2px 0;
`

const middleColumn = css`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

const teamsRow = css`
  display: flex;
  align-items: stretch;
  flex: 1;
  justify-content: center;
`

/* Team block: full team-color background containing abbr + score */
const teamBlock = css`
  display: flex;
  align-items: stretch;
  flex-shrink: 0;
`

const teamAbbr = css`
  ${font.title};
  font-size: 24px;
  font-weight: 600;
  color: ${colors.WHITE};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
`

/* Score gets a semi-dark overlay on top of the team color */
const scoreInner = css`
  display: flex;
  align-items: center;
  justify-content: center;
  ${font.title};
  font-size: 32px;
  font-weight: 600;
  color: ${colors.WHITE};
  width: 70px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.25);
`

const divider = css`
  width: 1px;
  background: rgba(255, 255, 255, 0.2);
  margin: 6px 0;
  flex-shrink: 0;
`

const timeCell = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 70px;
  justify-content: center;
  padding: 4px 12px;
  flex-shrink: 0;
`

const timeText = css`
  ${font.title};
  font-size: 18px;
  font-weight: 600;
  color: ${colors.HEADER};
  white-space: nowrap;
  text-transform: uppercase;
`

const periodText = css`
  ${font.title};
  font-size: 18px;
  font-weight: 600;
  color: ${colors.HEADER};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const shotsRow = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 2px 12px 3px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`

const shotsNumber = css`
  ${font.body};
  font-size: 16px;
  font-weight: 700;
  color: ${colors.SECONDARY_300};
`

const shotsLabel = css`
  font-size: 14px;
  font-weight: 400;
  color: ${colors.SECONDARY_300};
  text-transform: uppercase;
  letter-spacing: 0.8px;
`

const badgeStyle = css`
  display: inline-block;
  background: ${colors.NEUTRAL_600};
  color: ${colors.WHITE};
  font-size: 9px;
  font-weight: 600;
  padding: 2px 4px;
  border-radius: 2px;
  text-transform: uppercase;
`

const finalRow = css`
  display: flex;
  align-items: center;
  gap: 4px;
`

const LiveScoreWidget = ({
  game,
  videoUrl,
  homeTeamName,
  visitorTeamName,
  videoRef,
  videoReady,
  selectedStreamType,
  isLive,
  wsTimelineEvents,
  wsGameState,
  scoreboardEvents,
  initialScoreState,
  refetchScoreboard,
  homeJerseyColor,
  visitorJerseyColor,
}) => {
  // Sync scoreboard with video playback position
  const {
    homeScore: syncedHomeScore,
    visitorScore: syncedVisitorScore,
    homeShots: syncedHomeShots,
    visitorShots: syncedVisitorShots,
    displayTimer: syncedTimer,
    displayPeriod: syncedPeriod,
    isPastLastEvent,
  } = useVideoSyncedScore({
    videoRef,
    videoReady,
    game,
    videoUrl,
    selectedStreamType,
    isLive,
    scoreboardEvents,
    initialState: initialScoreState,
    wsTimelineEvents,
    wsGameState,
    refetchScoreboard,
  })

  // Resolve livestream timestamps for animation hook (must be before early return)
  const livestream = videoUrl
    ? game?.livestreams?.find((ls) => ls.stream_url === videoUrl)
    : game?.livestreams?.find((ls) => ls.type === selectedStreamType)
  const streamStartedAt = livestream?.stream_started_at

  // Scoreboard animations (goal/period cards)
  const {
    activeGoalCard,
    activePeriodCard,
    activeSubstitutionCard,
    activeFoulCard,
  } = useScoreboardAnimations({
    scoreboardEvents,
    videoRef,
    streamStartedAt,
    isLive,
    game,
    homeJerseyColor,
    visitorJerseyColor,
  })

  // Sport identity + display terminology (must be before early return)
  const { isGeneric: isGenericSport, terminology } = useGameSport({ game })

  if (!game) {
    return null
  }

  const [defaultHomeShots, defaultVisitorShots] = countTeamShots(
    game.homeTeamId,
    game.visitingTeamId,
    game.shots
  )

  const homeShots = syncedHomeShots ?? defaultHomeShots
  const visitorShots = syncedVisitorShots ?? defaultVisitorShots
  const displayHomeScore = syncedHomeScore ?? game.homeTeamScore ?? 0
  const displayVisitorScore = syncedVisitorScore ?? game.visitingTeamScore ?? 0

  const { completed, started } = game
  const homeAbbr =
    game?.homeTeam?.abbrev || homeTeamName?.slice(0, 3).toUpperCase() || 'HOM'
  const visitorAbbr =
    game?.visitingTeam?.abbrev ||
    visitorTeamName?.slice(0, 3).toUpperCase() ||
    'VIS'
  const homeTextColor = getContrastTextColor(homeJerseyColor)
  const visitorTextColor = getContrastTextColor(visitorJerseyColor)

  // The venue's clock, not the browser's -- and naming the zone is what lets
  // the permanent-DST correction apply, so a BC game after 2026-11-01 reads on
  // the offset the province kept. See common/ts-utils/dayjs-permanent-dst.
  //
  // getDayjsTZDate hands back its input untouched when there is no usable date
  // string, so the dayjs fallback keeps a game with no start from throwing on
  // .format() -- exactly what a bare dayjs(startsAt) did before.
  const startTime =
    getDayjsTZDate(game.startsAt, game?.location?.timezone) ||
    dayjs(game.startsAt)

  const renderTimeContent = () => {
    if (syncedTimer != null && (isLive || !isPastLastEvent)) {
      const periodLabel =
        syncedPeriod ||
        (!scoreboardEvents?.length
          ? startTime.format('MMM DD')
          : isGenericSport && syncedTimer === 'Final'
          ? 'Final'
          : '')
      return (
        <>
          <span css={periodText}>{periodLabel}</span>
          {periodLabel && !isGenericSport && <div css={clockDivider} />}
          {!isGenericSport && <span css={timeText}>{syncedTimer}</span>}
        </>
      )
    }
    if (completed) {
      const isOT = game.isOT || game.overtime
      const isSO = game.isSO || game.shootout
      return (
        <div css={finalRow}>
          <span css={timeText}>Final</span>
          {isOT && <span css={badgeStyle}>OT</span>}
          {isSO && <span css={badgeStyle}>SO</span>}
        </div>
      )
    }
    if (started) {
      return (
        <>
          {game.currentPeriodName ? (
            <>
              <span css={periodText}>{game.currentPeriodName}</span>
              {!isGenericSport && <div css={clockDivider} />}
            </>
          ) : null}
          {!isGenericSport && (
            <span css={timeText}>{game.currentPeriodTime || '--:--'}</span>
          )}
        </>
      )
    }
    return (
      <>
        <span css={periodText}>{startTime.format('MMM DD')}</span>
        <div css={clockDivider} />
        <span css={timeText}>{startTime.format('h:mm A')}</span>
      </>
    )
  }

  const scheduleImageUrl = resolveAssetUrl(game?.rootSchedule?.image)

  return (
    <>
      <div css={widgetContainer}>
        {scheduleImageUrl ? (
          <div css={logoCell}>
            <img
              css={leagueLogo}
              src={scheduleImageUrl}
              alt='League logo'
              width={36}
              height={36}
            />
          </div>
        ) : null}
        <div css={middleColumn}>
          <div css={teamsRow}>
            <div
              css={css`
                ${teamBlock};
                background: ${homeJerseyColor};
              `}
            >
              <span
                css={css`
                  ${teamAbbr};
                  color: ${homeTextColor};
                `}
              >
                {homeAbbr}
              </span>
              <div
                css={css`
                  ${scoreInner};
                  color: ${homeTextColor};
                `}
              >
                <AnimatedScore value={displayHomeScore} />
              </div>
            </div>
            <div
              css={css`
                ${teamBlock};
                background: ${visitorJerseyColor};
              `}
            >
              <div
                css={css`
                  ${scoreInner};
                  color: ${visitorTextColor};
                `}
              >
                <AnimatedScore value={displayVisitorScore} />
              </div>
              <span
                css={css`
                  ${teamAbbr};
                  color: ${visitorTextColor};
                `}
              >
                {visitorAbbr}
              </span>
            </div>
          </div>
          <div css={shotsRow}>
            <span css={shotsNumber}>{homeShots}</span>
            <span css={shotsLabel}>Shots</span>
            <span css={shotsNumber}>{visitorShots}</span>
          </div>
        </div>
        <div css={divider} />
        <div css={timeCell}>{renderTimeContent()}</div>
      </div>

      {activeGoalCard && (
        <GoalScoredCard
          genericSport={isGenericSport}
          terminology={terminology}
          key={activeGoalCard.id}
          data={activeGoalCard}
        />
      )}
      {activePeriodCard && (
        <PeriodCard key={activePeriodCard.id} data={activePeriodCard} />
      )}
      {activeSubstitutionCard && (
        <SubstitutionCard
          key={activeSubstitutionCard.id}
          data={activeSubstitutionCard}
          genericSport={isGenericSport}
          terminology={terminology}
        />
      )}
      {activeFoulCard && (
        <FoulCard
          key={activeFoulCard.id}
          data={activeFoulCard}
          genericSport={isGenericSport}
          terminology={terminology}
        />
      )}
    </>
  )
}

LiveScoreWidget.propTypes = {
  game: PropTypes.object,
  videoUrl: PropTypes.string,
  homeTeamName: PropTypes.string,
  visitorTeamName: PropTypes.string,
  videoRef: PropTypes.object,
  videoReady: PropTypes.bool,
  selectedStreamType: PropTypes.string,
  isLive: PropTypes.bool,
  wsTimelineEvents: PropTypes.object,
  wsGameState: PropTypes.object,
  scoreboardEvents: PropTypes.array,
  initialScoreState: PropTypes.object,
  refetchScoreboard: PropTypes.func,
  homeJerseyColor: PropTypes.string,
  visitorJerseyColor: PropTypes.string,
}

export default LiveScoreWidget
